import React, { useEffect, useState } from 'react';

import { Popover } from 'antd';

import styled, { css, keyframes } from 'styled-components';

import { useModelBooksPreview } from '@hooks/API/Contact/UseModelBooksPreview';

import ModelBooksPreviewRows from '@components/ActivePackages/ModelBooksPreviewRows';
import NwIcon from '@components/Gui/NwIcon';

/** Sotto questa permanenza non si apre niente: attraversare il wall non chiama il server. */
const INTENT_DELAY_SECONDS = 0.18;

/** Dove le icone sono affiancate e strette serve piu' intenzione per non farle sparare. */
export const DENSE_INTENT_DELAY = 0.5;

/**
 * Quanto la pillola resta da sola prima di crescere. Serve anche quando i book sono gia'
 * in cache e arrivano subito: senza, l'anteprima sboccerebbe di colpo e sembrerebbe un
 * salto invece di una crescita.
 */
const MIN_DWELL = 900;

const shuttle = keyframes`
    from { transform: translateX(-100%); }
    to { transform: translateX(363%); }
`;

const PreviewCard = styled.div`
    width: 19rem;
    max-width: 90vw;

    .preview-header {
        display: flex;
        gap: 0.55rem;
        align-items: flex-start;
        padding: 0.65rem 0.85rem 0.55rem;
    }

    .preview-header > svg {
        flex: none;
        margin-top: 0.15rem;
        color: ${(props) => props.theme.PrimaryColor};
    }

    .preview-label {
        font-size: 0.8rem;
        font-weight: 600;
        letter-spacing: 0.045em;
        line-height: 1.3;
        text-transform: uppercase;
        color: ${(props) => props.theme.TextColor};
    }

    .preview-destination {
        margin-top: 0.1rem;
        font-size: ${(props) => props.theme.FontSizeSm};
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: ${(props) => props.theme.TextColorMuted};
    }

    /* La barra di caricamento si posa a tutta larghezza e resta come divisore. */
    .preview-rule {
        height: 2px;
        position: relative;
        overflow: hidden;
        background: ${(props) => props.theme.PrimaryColorLight};

        &::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 38%;
            background: ${(props) => props.theme.PrimaryColor};
            animation: ${shuttle} 1150ms cubic-bezier(0.5, 0, 0.5, 1) infinite;
        }
    }

    .preview-body {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
    }

    .preview-body > div {
        overflow: hidden;
    }

    .preview-list {
        padding: 0 0.85rem 0.5rem;
        max-height: min(46vh, 22rem);
        overflow-y: auto;
    }

    ${(props) =>
        props.$settled &&
        css`
            .preview-rule::after {
                animation: none;
                width: 100%;
            }
        `}

    ${(props) =>
        props.$open &&
        css`
            .preview-body {
                grid-template-rows: 1fr;
            }
        `}

    @media (prefers-reduced-motion: reduce) {
        .preview-body {
            transition: none;
        }
        .preview-rule::after {
            animation: none;
        }
    }
`;

/* Il Popover ha bisogno di un nodo del DOM: NwIcon da solo non lo garantisce. */
const PreviewTrigger = styled.span`
    display: inline-flex;
    align-items: center;
    cursor: inherit;
`;

const PreviewEmpty = styled.div`
    padding: 0 0.85rem 0.75rem;
    font-size: ${(props) => props.theme.FontSizeSm};
    color: ${(props) => props.theme.TextColorMuted};
`;

/**
 * Anteprima dei book che il pulsante aggiungerebbe al package, in hover sull'icona.
 * Una superficie sola in due taglie: la pillola e' il popover alla sua dimensione minima
 * e cresce quando i book sono arrivati, invece di sostituire un tooltip con un popover.
 */
const AddModelBooksPreviewPopover = ({
    variant,
    modelId,
    icon,
    label,
    destination,
    intentDelay = INTENT_DELAY_SECONDS,
    children,
}) => {
    const [open, setOpen] = useState(false);
    const [dwellDone, setDwellDone] = useState(false);
    const { books, isLoading, isError } = useModelBooksPreview(variant, modelId, open);

    useEffect(() => {
        if (!open) {
            setDwellDone(false);
            return undefined;
        }
        const timer = setTimeout(() => setDwellDone(true), MIN_DWELL);
        return () => clearTimeout(timer);
    }, [open]);

    const settled = !isLoading && !isError;
    const expanded = open && dwellDone && settled;

    const content = (
        <PreviewCard $open={expanded} $settled={settled}>
            <div className="preview-header">
                <NwIcon icon={icon} />
                <div>
                    <div className="preview-label">{label}</div>
                    {destination && <div className="preview-destination">{destination}</div>}
                </div>
            </div>
            <div className="preview-rule" />
            <div className="preview-body">
                <div>
                    {books.length > 0 ? (
                        <div className="preview-list">
                            <ModelBooksPreviewRows books={books} showOrder={false} />
                        </div>
                    ) : (
                        <PreviewEmpty>No books to preview</PreviewEmpty>
                    )}
                </div>
            </div>
        </PreviewCard>
    );

    return (
        <Popover
            trigger="hover"
            placement="leftTop"
            // Se l'anteprima fallisce il popover sparisce: l'icona aggiunge comunque i book.
            open={open && !isError}
            onOpenChange={setOpen}
            mouseEnterDelay={intentDelay}
            mouseLeaveDelay={0.1}
            destroyTooltipOnHide
            overlayClassName="folder-popover"
            content={content}
        >
            <PreviewTrigger>{children}</PreviewTrigger>
        </Popover>
    );
};

export default AddModelBooksPreviewPopover;
